import React, { useEffect, useRef } from 'react';

import css from './AuthKulisse.module.css';

/**
 * FairwayBid – Höhenlinien als Kulisse
 *
 * Auf Anmelde- und Registrierungsseiten steht sonst ein Foto: ein gemähtes
 * Fairway im Abendlicht. Das sieht ein Clubmanager jeden Tag, und es sagt ihm
 * nichts über die Seite, auf der er gerade sein Passwort eingibt.
 *
 * Höhenlinien sagen etwas. Sie sind das, was ein Platzarchitekt zeichnet und
 * ein Greenkeeper liest – eindeutig Golf, ohne Postkarte zu sein. Und sie
 * sehen aus wie nichts anderes in dieser Branche.
 *
 * Wie sie entstehen
 * -----------------
 * Unter der Fläche liegt ein Höhenfeld aus einer Handvoll weicher Hügel. Für
 * jede Höhenstufe wird die Linie gesucht, an der das Feld genau diesen Wert
 * hat – dasselbe Verfahren, mit dem eine Landkarte entsteht (Marching
 * Squares). Die Hügel wandern langsam auf eigenen Bahnen, und weil sich das
 * Feld dabei verformt, wandern die Linien mit: Das Gelände atmet, statt sich
 * zu drehen.
 *
 * Jede fünfte Linie ist kräftiger. So macht es eine topografische Karte auch,
 * und ohne diese Abstufung wird ein Linienfeld schnell zum Moiré.
 *
 * Was hier Rücksicht kostet
 * -------------------------
 *   - Gerechnet wird alle drei Bilder. Das Gelände bewegt sich so langsam,
 *     dass niemand den Unterschied sieht, und der Rechner hat zwei Drittel
 *     mehr Ruhe.
 *   - Das Gitter ist grob (rund 14 Pixel pro Zelle) und die Linien werden
 *     nicht geglättet: In dieser Größe sieht man die Kanten nicht, aber man
 *     merkt den Unterschied im Lüfter.
 *   - Die Schleife hält an, sobald die Seite in den Hintergrund geht.
 *   - Wer Bewegung abgestellt hat, bekommt ein einziges stehendes Bild.
 *
 * Über der Kulisse liegt das Anmeldeformular auf eigenem, deckendem Grund.
 * Nichts von alledem liegt hinter einem Eingabefeld - ein bewegter Grund
 * unter einem Passwortfeld waere kein Design, sondern ein Hindernis.
 */

// Sechs Hügel reichen für ein Gelände, das nicht wie eine Zielscheibe wirkt.
const HUEGEL = [
  { hoehe: 1.0, breite: 0.34, ax: 0.16, ay: 0.1, tx: 61, ty: 83, px: 0.3, py: 0.36 },
  { hoehe: 0.78, breite: 0.26, ax: 0.13, ay: 0.15, tx: 79, ty: 67, px: 0.72, py: 0.28 },
  { hoehe: -0.62, breite: 0.3, ax: 0.11, ay: 0.12, tx: 97, ty: 53, px: 0.52, py: 0.74 },
  { hoehe: 0.54, breite: 0.2, ax: 0.15, ay: 0.09, tx: 47, ty: 101, px: 0.86, py: 0.66 },
  { hoehe: -0.44, breite: 0.22, ax: 0.1, ay: 0.14, tx: 113, ty: 71, px: 0.14, py: 0.78 },
  { hoehe: 0.42, breite: 0.18, ax: 0.12, ay: 0.11, tx: 59, ty: 89, px: 0.42, py: 0.14 },
];

const ZELLE = 14; // Gitterweite in Pixeln
const STUFEN = 16; // so viele Höhenlinien
const TAKT = 3; // nur jedes dritte Bild neu rechnen

/*
  Marching Squares: Welche Kanten einer Zelle die Linie schneidet, je nachdem
  welche Ecken über der Höhe liegen. Ecken im Uhrzeigersinn ab links oben,
  Kanten: 0 oben, 1 rechts, 2 unten, 3 links.
*/
const FAELLE = [
  [],
  [3, 0],
  [0, 1],
  [3, 1],
  [1, 2],
  [3, 0, 1, 2],
  [0, 2],
  [3, 2],
  [2, 3],
  [2, 0],
  [0, 1, 2, 3],
  [2, 1],
  [1, 3],
  [1, 0],
  [0, 3],
  [],
];

const Hoehenlinien = ({ className }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const ruhe =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let breite = 0;
    let hoehe = 0;
    let spalten = 0;
    let zeilen = 0;
    let feld = null;
    let laeuft = false;
    let bild = 0;
    let zaehler = 0;

    const messen = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const kasten = canvas.getBoundingClientRect();
      breite = Math.max(1, Math.round(kasten.width));
      hoehe = Math.max(1, Math.round(kasten.height));
      canvas.width = Math.round(breite * dpr);
      canvas.height = Math.round(hoehe * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      spalten = Math.ceil(breite / ZELLE) + 1;
      zeilen = Math.ceil(hoehe / ZELLE) + 1;
      feld = new Float32Array(spalten * zeilen);
    };

    // Höhe an einer Stelle: die Summe aller Hügel, jeder eine weiche Glocke.
    const feldFuellen = t => {
      const mittel = [];
      for (let h = 0; h < HUEGEL.length; h += 1) {
        const g = HUEGEL[h];
        mittel.push({
          x: (g.px + Math.sin(t / g.tx) * g.ax) * breite,
          y: (g.py + Math.cos(t / g.ty) * g.ay) * hoehe,
          r2: 2 * Math.pow(g.breite * Math.min(breite, hoehe), 2),
          a: g.hoehe,
        });
      }

      for (let j = 0; j < zeilen; j += 1) {
        const y = j * ZELLE;
        for (let i = 0; i < spalten; i += 1) {
          const x = i * ZELLE;
          let summe = 0;
          for (let h = 0; h < mittel.length; h += 1) {
            const m = mittel[h];
            const dx = x - m.x;
            const dy = y - m.y;
            summe += m.a * Math.exp(-(dx * dx + dy * dy) / m.r2);
          }
          feld[j * spalten + i] = summe;
        }
      }
    };

    const zeichnen = () => {
      ctx.clearRect(0, 0, breite, hoehe);
      ctx.lineJoin = 'round';

      for (let s = 0; s < STUFEN; s += 1) {
        // Von unten nach oben durch das Gelaende schneiden.
        const stufe = -0.55 + (s * 1.4) / STUFEN;
        // Jede fuenfte Linie kraeftiger - wie auf einer Wanderkarte.
        const kraeftig = s % 5 === 0;
        ctx.lineWidth = kraeftig ? 1.5 : 1;
        ctx.strokeStyle = kraeftig ? 'rgba(122, 214, 160, 0.34)' : 'rgba(122, 214, 160, 0.16)';
        ctx.beginPath();

        for (let j = 0; j < zeilen - 1; j += 1) {
          for (let i = 0; i < spalten - 1; i += 1) {
            const a = feld[j * spalten + i];
            const b = feld[j * spalten + i + 1];
            const c = feld[(j + 1) * spalten + i + 1];
            const d = feld[(j + 1) * spalten + i];

            const fall =
              (a > stufe ? 1 : 0) | (b > stufe ? 2 : 0) | (c > stufe ? 4 : 0) | (d > stufe ? 8 : 0);
            const kanten = FAELLE[fall];
            if (kanten.length === 0) continue;

            const x0 = i * ZELLE;
            const y0 = j * ZELLE;

            // Schnittpunkt auf einer Kante, linear zwischen den Eckwerten.
            const punkt = kante => {
              switch (kante) {
                case 0:
                  return [x0 + ZELLE * ((stufe - a) / (b - a || 1e-6)), y0];
                case 1:
                  return [x0 + ZELLE, y0 + ZELLE * ((stufe - b) / (c - b || 1e-6))];
                case 2:
                  return [x0 + ZELLE * ((stufe - d) / (c - d || 1e-6)), y0 + ZELLE];
                default:
                  return [x0, y0 + ZELLE * ((stufe - a) / (d - a || 1e-6))];
              }
            };

            for (let k = 0; k < kanten.length; k += 2) {
              const p = punkt(kanten[k]);
              const q = punkt(kanten[k + 1]);
              ctx.moveTo(p[0], p[1]);
              ctx.lineTo(q[0], q[1]);
            }
          }
        }
        ctx.stroke();
      }
    };

    const schritt = () => {
      if (!laeuft) return;
      zaehler += 1;
      if (zaehler % TAKT === 0) {
        feldFuellen(zaehler / TAKT);
        zeichnen();
      }
      bild = requestAnimationFrame(schritt);
    };

    const anhalten = () => {
      laeuft = false;
      cancelAnimationFrame(bild);
    };

    const starten = () => {
      if (laeuft || ruhe) return;
      laeuft = true;
      bild = requestAnimationFrame(schritt);
    };

    const stehendesBild = () => {
      feldFuellen(0);
      zeichnen();
    };

    messen();
    stehendesBild();
    if (!ruhe) starten();

    const beiTabwechsel = () => {
      if (document.hidden) anhalten();
      else starten();
    };
    document.addEventListener('visibilitychange', beiTabwechsel);

    let messTimer = null;
    const beiGroesse = () => {
      clearTimeout(messTimer);
      messTimer = setTimeout(() => {
        messen();
        stehendesBild();
      }, 180);
    };
    window.addEventListener('resize', beiGroesse);

    return () => {
      anhalten();
      clearTimeout(messTimer);
      document.removeEventListener('visibilitychange', beiTabwechsel);
      window.removeEventListener('resize', beiGroesse);
    };
  }, []);

  return <canvas ref={canvasRef} className={className || css.linien} aria-hidden="true" />;
};

export default Hoehenlinien;
